import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import {
  type IParamsFiltersListGroups,
  type IResponseBroadcastById,
  type IResponseInvalidBreakdown,
} from 'entities/Broadcast/api/types'
import { ContactsApi } from 'entities/Contacts'
import { Segment } from 'entities/Segment/model/Segment'
import { TagsApi } from 'entities/Tags'
import { Tag } from 'entities/Tags/model/Tag'
import { ContactFiltersApi } from 'entities/Contacts/api/contactsFilters'
import type { IFiltersListGroups } from 'entities/Contacts/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { EnrollmentFilters, EnrollmentStore } from 'widgets/ContactEnrollment'
import { type IEnrollmentActions } from 'widgets/ContactEnrollment/store/types'

export class BroadcastContactsStore {
  private _total = 0
  private _skipped = 0
  private _allTotal = 0
  private _enrollmentFilters = EnrollmentFilters.Empty()
  private _enrollmentExcludeFilters = EnrollmentFilters.Empty()
  private _enrollmentStore: EnrollmentStore
  private _loading = false
  private _number_id: number | null = null
  private _invalidBreakdown: IResponseInvalidBreakdown | null = null

  constructor(private _entities: IEntitiesStore) {
    this._enrollmentStore = new EnrollmentStore(
      {
        filterConfig: {
          makeRequest: () => {
            return ContactFiltersApi.getFilters({
              withCustomFields: true,
              withIntegrationList: true,
              withFullHubSpotList: true,
              skipValues: ['tag'],
            }).then(({ data }) => data)
          },
          useWidgetHubspotMulti: true,
        },
      },
      this._entities
    )

    makeAutoObservable(this)
  }

  get loading() {
    return this._loading
  }

  get total() {
    return this._total
  }

  get allTotal() {
    return this._allTotal
  }

  get skipped() {
    return this._skipped
  }

  get invalidBreakdown() {
    return this._invalidBreakdown
  }

  get enrollmentStore() {
    return this._enrollmentStore
  }

  get paramsFilters(): IParamsFiltersListGroups {
    return {
      filtersListGroups: {
        contacts: this._enrollmentFilters.contacts.map((contact) => contact.id) ?? [],
        segments: this._enrollmentFilters.segments?.map((segment) => segment.id) ?? [],
        tags: this._enrollmentFilters.tags?.map((tag) => tag.id),
        advanced: this._enrollmentFilters.filters?.map((filter) => filter.filtersParams),
      },
      excludeFiltersListGroups: {
        contacts: this._enrollmentExcludeFilters.contacts.map((contact) => contact.id) ?? [],
        segments: this._enrollmentExcludeFilters.segments?.map((segment) => segment.id) ?? [],
        tags: this._enrollmentExcludeFilters.tags?.map((tag) => tag.id),
        advanced: this._enrollmentExcludeFilters.filters?.map((filter) => filter.filtersParams),
      },
    }
  }

  get hasHubspotList() {
    const filtersListGroupsStr = JSON.stringify(this.paramsFilters.filtersListGroups)
    return (
      filtersListGroupsStr.includes('"integration_key":"hubspot-lists"') ||
      filtersListGroupsStr.includes('"integration_key":"hubspot"')
    )
  }

  get showZeroState() {
    return !this.allTotal && this.hasHubspotList
  }

  loadFilters = () => {
    this._enrollmentStore.initFilters()
  }

  private syncFiltersGroupsInto = async (target: EnrollmentFilters, data: IFiltersListGroups) => {
    const tagsIds = data.tags
    const contactsIds = data.contacts
    const segmentsIds = data.segments

    const tags = tagsIds ? await this.fetchTags(tagsIds) : []
    const contacts = contactsIds ? await this._entities.contactsStore.getByIds(contactsIds) : []

    const segments = segmentsIds?.reduce<Segment[]>((state, id) => {
      const item = this._entities.segmentsStore.getItem(id)

      if (item) {
        state.push(item)
      }

      return state
    }, [])

    const advanced = data.advanced?.map(
      (item) =>
        new Segment({
          id: -1,
          organization_id: 0,
          name: '',
          filters: item,
          is_favorite: false,
        })
    )

    target.sync({
      tags,
      segments,
      contacts,
      filters: advanced,
      number_id: this._number_id,
    })
  }

  init = async (response: IResponseBroadcastById) => {
    try {
      runInAction(() => {
        this._loading = true
      })

      const filtersListGroups = {
        advanced: response.filters.filtersListGroups.advanced,
        segments: response.filters.filtersListGroups.segments.map((item) =>
          typeof item === 'number' ? item : item.id
        ),
        tags: response.filters.filtersListGroups.tags.map((item) =>
          typeof item === 'number' ? item : item.id
        ),
        contacts: response.filters.filtersListGroups.contacts.map((item) =>
          typeof item === 'number' ? item : item.id
        ),
      }
      const excludeFiltersListGroupsRaw = response.filters.excludeFiltersListGroups
      const excludeFiltersListGroups = excludeFiltersListGroupsRaw
        ? {
            advanced: excludeFiltersListGroupsRaw.advanced,
            segments: excludeFiltersListGroupsRaw.segments.map((item) =>
              typeof item === 'number' ? item : item.id
            ),
            tags: excludeFiltersListGroupsRaw.tags.map((item) =>
              typeof item === 'number' ? item : item.id
            ),
            contacts: excludeFiltersListGroupsRaw.contacts.map((item) =>
              typeof item === 'number' ? item : item.id
            ),
          }
        : null

      this._number_id = response.filters.number_id

      this._total = response.contacts_count
      this._skipped = response.invalid_contacts_count
      this._allTotal = response.contacts_count + response.invalid_contacts_count
      this._invalidBreakdown = response.invalid_contacts_breakdown

      await this.syncFiltersGroupsInto(this._enrollmentFilters, filtersListGroups)

      if (excludeFiltersListGroups) {
        await this.syncFiltersGroupsInto(this._enrollmentExcludeFilters, excludeFiltersListGroups)
      }
    } catch (e) {
      console.log(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  fetchTags = async (ids: number[]) => {
    const tags = await TagsApi.getTagsById(ids)

    return tags.map((tag) => new Tag(tag))
  }

  confirmEnrollment = (total: number, skipped: number) => {
    return new Promise<boolean>((resolve) => {
      resolve(true)
      this._total = total
      this._skipped = skipped
      this._allTotal = total + skipped
    })
  }

  confirmEnrollmentCallback = async (
    isAllowed: boolean,
    newFilters: EnrollmentFilters,
    newExcludeFilters: EnrollmentFilters
  ) => {
    if (!isAllowed) return isAllowed

    runInAction(() => {
      this._enrollmentFilters = newFilters
      this._enrollmentExcludeFilters = newExcludeFilters
    })

    try {
      const { data } = await ContactsApi.getContactsFiltersCount({
        filtersListGroups: {
          contacts: newFilters.contacts.map((contact) => contact.id) ?? [],
          segments: newFilters.segments?.map((segment) => segment.id) ?? [],
          tags: newFilters.tags?.map((tag) => tag.id),
          advanced: newFilters.filters?.map((filter) => filter.filtersParams),
        },
        excludeFiltersListGroups: {
          contacts: newExcludeFilters.contacts.map((contact) => contact.id) ?? [],
          segments: newExcludeFilters.segments?.map((segment) => segment.id) ?? [],
          tags: newExcludeFilters.tags?.map((tag) => tag.id),
          advanced: newExcludeFilters.filters?.map((filter) => filter.filtersParams),
        },
        number_id: this._number_id || undefined,
      })

      runInAction(() => {
        this._invalidBreakdown = data.invalid_breakdown
      })
    } catch (error) {
      logger.error(error)
    }

    return isAllowed
  }

  editEnrollment = async () => {
    this._enrollmentFilters.sync({
      number_id: this._number_id,
    })
    this._enrollmentExcludeFilters.sync({
      number_id: this._number_id,
    })

    const filters = this._enrollmentFilters.clone()
    const excludeFilters = this._enrollmentExcludeFilters.clone()

    const cancelFilters = () => Promise.resolve(true)

    const confirmFilters: IEnrollmentActions['confirm'] = async ({ total, skipped }) => {
      const isAllowed = await this.confirmEnrollment(total, skipped)
      return this.confirmEnrollmentCallback(isAllowed, filters, excludeFilters)
    }

    this._enrollmentStore.openModal({
      filters: filters,
      excludeFilters: excludeFilters,
      action: {
        cancel: cancelFilters,
        confirm: confirmFilters,
      },
      title: 'Contacts',
      number_id: this._number_id,
    })
  }

  setInitContactIds = async (contact_ids: number[]) => {
    const [{ data }, contacts] = await Promise.all([
      ContactsApi.getContactsFiltersCount({
        filtersListGroups: {
          contacts: contact_ids,
        },
      }),
      this._entities.contactsStore.getByIds(contact_ids),
    ])
    runInAction(() => {
      this._total = data.count
      this._skipped = data.invalid_count
      this._allTotal = data.count + data.invalid_count
      this._invalidBreakdown = data.invalid_breakdown
    })
    if (contacts) {
      this._enrollmentFilters.sync({
        contacts: contacts,
        number_id: this._number_id,
      })
    }
  }

  setNumberId = (value: typeof this._number_id) => {
    this._number_id = value
  }

  loadCount = async (value: typeof this._number_id) => {
    const filters = this._enrollmentFilters.clone()
    const excludeFilters = this._enrollmentExcludeFilters.clone()

    this._enrollmentStore.initEnrollmentList({
      number_id: value,
      filters: filters,
      excludeFilters: excludeFilters,
      confirmEnrollment: async ({ total, skipped, invalid_breakdown }) => {
        runInAction(() => {
          this._total = total
          this._skipped = skipped
          this._allTotal = total + skipped
          this._invalidBreakdown = invalid_breakdown
        })
        return false
      },
    })
  }

  setInitStoredFiltersListGroups = async (filtersListGroups: IFiltersListGroups) => {
    try {
      const { data } = await ContactsApi.getContactsFiltersCount({
        filtersListGroups: filtersListGroups,
      })

      runInAction(() => {
        this._total = data.count
        this._skipped = data.invalid_count
        this._allTotal = data.count + data.invalid_count
        this._invalidBreakdown = data.invalid_breakdown
      })
    } catch (error) {
      throw error
    } finally {
      this.syncFiltersListGroups(filtersListGroups).then(() => this.loadCount(this._number_id))
    }
  }

  syncFiltersListGroups = async (data: IFiltersListGroups) => {
    runInAction(() => {
      this._loading = true
    })

    try {
      await this.syncFiltersGroupsInto(this._enrollmentFilters, data)
    } catch (e) {
      console.log(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }
}
